/**
 * ZUG Cart Bundle Manager v6
 * Comprehensive cart bundle handling for V2 pricing engine.
 */
(function() {
  'use strict';

  function hasPrivateProductionQuantity(properties) {
    var props = properties || {};
    return Boolean(props.Pieces || (props['Total Pieces'] && props['_bogo_free_pieces']));
  }

  function getLineUid(properties) {
    var props = properties || {};
    var publicUid = String(props.calc_line_uid || '').trim();
    if (publicUid) return publicUid;
    if (!hasPrivateProductionQuantity(props) &&
        props._carrier_strategy !== 'shared_native_sticker_units' &&
        props._carrier_strategy !== 'acrylic_keychain_native_units' &&
        props._carrier_strategy !== 'products2_native_rate_units' &&
        props._carrier_strategy !== 'native_configured_order_variant') return '';
    return String(props._calc_line_uid || '').trim();
  }

  function usesEncodedNativeQuantity(properties) {
    var props = properties || {};
    var strategy = props._carrier_strategy;
    return strategy === 'shared_native_sticker_units' ||
      strategy === 'acrylic_keychain_native_units' ||
      strategy === 'products2_native_rate_units' ||
      Boolean(String(props._calc_line_uid || '').trim() && hasPrivateProductionQuantity(props));
  }

  if (window.__ZUG_CART_BUNDLE_LOADED__) return;
  window.__ZUG_CART_BUNDLE_LOADED__ = true;

  var ZugCartBundle = {
    initialized: false,
    isProcessing: false,
    cartCache: null,
    cartCacheTime: 0,
    cartCacheTTL: 2000,
    inFlightPromise: null,
    forceFreshFetch: false,
    mutationLocks: {},
    observedEls: null,
    warnedPriceMismatches: new Set(),
    isApplyingUI: false,
    lastRefreshTs: 0,
    refreshThrottleMs: 250,

    invalidateCartCache: function() {
      this.cartCache = null;
      this.cartCacheTime = 0;
      this.forceFreshFetch = true;
    },

    getCartFresh: function() {
      this.invalidateCartCache();
      return this.getCartOnceDebounced();
    },

    getCartOnceDebounced: function() {
      var self = this;
      var now = Date.now();

      if (self.inFlightPromise) {
        return self.inFlightPromise;
      }

      var cacheValid = self.cartCache && 
                       (now - self.cartCacheTime) < self.cartCacheTTL && 
                       !self.forceFreshFetch;

      if (cacheValid) {
        return Promise.resolve(self.cartCache);
      }

      self.forceFreshFetch = false;

      self.inFlightPromise = fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) {
          if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
          return r.json();
        })
        .then(function(cart) {
          self.cartCache = cart;
          self.cartCacheTime = Date.now();
          self.inFlightPromise = null;
          return cart;
        })
        .catch(function(e) {
          self.inFlightPromise = null;
          console.error('[ZUG][BUNDLE] cart fetch error:', e);
          throw e;
        });

      return self.inFlightPromise;
    },

    groupCart: function(cart) {
      var groups = {};
      var baseOnlyQty = 0;
      var nonEngineQty = 0;

      cart.items.forEach(function(item) {
        var props = item.properties || {};
        var uid = getLineUid(props);
        var isPrivateUidLine = Boolean(
          String(props._calc_line_uid || '').trim() && hasPrivateProductionQuantity(props)
        );
        var lineType = props._line_type || (isPrivateUidLine ? 'base' : '');
        var isEngine = props._pricing_engine === 'true' || isPrivateUidLine;

        if (uid && isEngine) {
          if (!groups[uid]) {
            groups[uid] = {
              totalCents: 0,
              baseKey: null,
              baseQty: 0,
              keys: [],
              hasBase: false
            };
          }

          groups[uid].totalCents += item.final_line_price;
          groups[uid].keys.push(item.key);

          if (lineType === 'base') {
            groups[uid].baseKey = item.key;
            groups[uid].baseQty = item.quantity;
            groups[uid].hasBase = true;
            baseOnlyQty += usesEncodedNativeQuantity(props)
              ? 1
              : item.quantity;
          }
        } else if (!isEngine) {
          nonEngineQty += item.quantity;
        }
      });

      return {
        groups: groups,
        baseOnlyQty: baseOnlyQty + nonEngineQty,
        uidCount: Object.keys(groups).length
      };
    },

    formatMoney: function(cents) {
      var dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    },

    updateLineTotals: function(groups) {
      var self = this;
      var count = 0;

      document.querySelectorAll('[data-zug-line-type="base"][data-zug-uid]').forEach(function(el) {
        var uid = el.dataset.zugUid;
        var group = groups[uid];
        if (!group) return;

        var formattedPrice = self.formatMoney(group.totalCents);

        el.querySelectorAll('[data-zug-display-total]').forEach(function(displayEl) {
          displayEl.textContent = formattedPrice;
          displayEl.setAttribute('data-zug-displayed-cents', String(group.totalCents));
          count++;
        });
      });

      document.querySelectorAll('tr[data-zug-line-type="base"][data-zug-uid]').forEach(function(row) {
        var uid = row.dataset.zugUid;
        var group = groups[uid];
        if (!group) return;

        var formattedPrice = self.formatMoney(group.totalCents);

        row.querySelectorAll('[data-zug-display-total]').forEach(function(totalCell) {
          totalCell.textContent = formattedPrice;
          totalCell.setAttribute('data-zug-displayed-cents', String(group.totalCents));
          count++;
        });
      });

      return count;
    },

    checkPriceInvariant: function(cart) {
      var self = this;
      var grouped = self.groupCart(cart).groups;
      cart.items.forEach(function(item) {
        var props = item.properties || {};
        var privateUid = String(props._calc_line_uid || '').trim();
        if (privateUid && props.Pieces && props._pricing_engine !== 'true') {
          var nativeExpectedTotalCents = Number(item.price) * Number(item.quantity);
          var nativeActualTotalCents = Number(item.final_line_price);
          if (Number.isFinite(nativeExpectedTotalCents) &&
              nativeExpectedTotalCents !== nativeActualTotalCents) {
            var nativeSig = [
              'private-native', item.key, item.id, privateUid,
              nativeExpectedTotalCents, nativeActualTotalCents
            ].join('|');
            if (self.warnedPriceMismatches.has(nativeSig)) return;
            self.warnedPriceMismatches.add(nativeSig);
            console.warn('[ZUG][PRICE_ASSERT]', {
              family: 'products-2-0-native-rate',
              uid: privateUid,
              expectedTotalCents: nativeExpectedTotalCents,
              actualTotalCents: nativeActualTotalCents,
              diffLine: nativeActualTotalCents - nativeExpectedTotalCents
            });
          }
          return;
        }
        if (props._pricing_engine !== 'true' || props._line_type !== 'base') return;

        var uid = getLineUid(props);
        if (props._product_family === 'dtf' && props._dtf_exact_price_bundle === 'true') {
          var dtfUnitTotalCents = Number(props._dtf_unit_total_cents);
          var dtfExpectedTotalCents = dtfUnitTotalCents * Number(item.quantity);
          var dtfActualTotalCents = grouped[uid] ? grouped[uid].totalCents : NaN;
          if (Number.isInteger(dtfUnitTotalCents) && dtfUnitTotalCents > 0 &&
              dtfExpectedTotalCents !== dtfActualTotalCents) {
            var dtfSig = ['dtf-gang-sheet', item.key, uid, dtfExpectedTotalCents, dtfActualTotalCents].join('|');
            if (self.warnedPriceMismatches.has(dtfSig)) return;
            self.warnedPriceMismatches.add(dtfSig);
            console.warn('[ZUG][PRICE_ASSERT]', {
              family: 'dtf-gang-sheet',
              uid: uid,
              expectedTotalCents: dtfExpectedTotalCents,
              actualTotalCents: dtfActualTotalCents,
              diffLine: dtfActualTotalCents - dtfExpectedTotalCents
            });
          }
          return;
        }

        var calcData = null;
        if (props._calc_data) {
          try {
            calcData = JSON.parse(props._calc_data);
          } catch (error) {
            console.warn('[ZUG][PRICE_ASSERT] Invalid _calc_data JSON', { uid: uid });
          }
        }
        if (calcData && Number.isFinite(Number(calcData.total))) {
          var compactExpectedTotalCents = Number(calcData.total);
          var compactActualTotalCents = grouped[uid] ? grouped[uid].totalCents : NaN;
          if (compactExpectedTotalCents !== compactActualTotalCents) {
            var compactSig = ['calc-data', item.key, item.id, uid, compactExpectedTotalCents, compactActualTotalCents].join('|');
            if (self.warnedPriceMismatches.has(compactSig)) return;
            self.warnedPriceMismatches.add(compactSig);
            console.warn('[ZUG][PRICE_ASSERT]', {
              family: calcData.f || 'products-2-0',
              uid: uid,
              expectedTotalCents: compactExpectedTotalCents,
              actualTotalCents: compactActualTotalCents,
              diffLine: compactActualTotalCents - compactExpectedTotalCents
            });
          }
          return;
        }
        if (props._carrier_strategy === 'native_product_base_plus_exact_cent_adjustments') {
          var expectedTotalCents = Math.round(parseFloat(props._total_desired) * 100);
          var actualTotalCents = grouped[uid] ? grouped[uid].totalCents : NaN;

          if (Number.isFinite(expectedTotalCents) && expectedTotalCents !== actualTotalCents) {
            var acrylicSig = ['acrylic', item.key, item.id, uid, expectedTotalCents, actualTotalCents].join('|');
            if (self.warnedPriceMismatches.has(acrylicSig)) return;
            self.warnedPriceMismatches.add(acrylicSig);
            console.warn('[ZUG][PRICE_ASSERT]', {
              family: 'acrylic',
              uid: uid,
              expectedTotalCents: expectedTotalCents,
              actualTotalCents: actualTotalCents,
              diffLine: actualTotalCents - expectedTotalCents
            });
          }
          return;
        }

        var compactUnit = calcData && calcData.unit;
        var legacyUnit = compactUnit == null ? props._u_final : compactUnit;
        if (legacyUnit == null || legacyUnit === '') return;
        var expectedUnitCents = Math.round(parseFloat(legacyUnit) * 100);
        var actualUnitCents = item.price;

        if (expectedUnitCents !== actualUnitCents) {
          var sig = [item.key, item.id, uid, expectedUnitCents, actualUnitCents].join('|');
          if (self.warnedPriceMismatches.has(sig)) return;
          self.warnedPriceMismatches.add(sig);

          console.warn('[ZUG][PRICE_ASSERT]', {
            sku: item.sku || '',
            uid: uid,
            sizeTier: calcData && calcData.tier != null ? calcData.tier : (props._size_tier_in2 || ''),
            qty: item.quantity,
            expectedUnitCents: expectedUnitCents,
            actualUnitCents: actualUnitCents,
            diffPerUnit: actualUnitCents - expectedUnitCents,
            diffLine: item.final_line_price - Math.round(expectedUnitCents * item.quantity)
          });
        }
      });
    },

    checkInvariant: function(groups) {
      document.querySelectorAll('[data-zug-line-type="base"][data-zug-uid]').forEach(function(el) {
        var uid = el.dataset.zugUid;
        var group = groups[uid];
        if (!group) return;

        var displayEl = el.querySelector('[data-zug-display-total]');
        if (!displayEl) return;

        var displayedCentsAttr = displayEl.getAttribute('data-zug-displayed-cents');
        if (!displayedCentsAttr) return;

        var displayedCents = parseInt(displayedCentsAttr, 10);
        if (displayedCents !== group.totalCents) {
          console.warn('[ZUG][ASSERT] UI total mismatch', {
            uid: uid,
            displayed: displayedCents,
            computed: group.totalCents
          });
        }
      });

      document.querySelectorAll('tr[data-zug-line-type="base"][data-zug-uid]').forEach(function(row) {
        var uid = row.dataset.zugUid;
        var group = groups[uid];
        if (!group) return;

        var totalCell = row.querySelector('[data-zug-display-total]');
        if (!totalCell) return;

        var displayedCentsAttr = totalCell.getAttribute('data-zug-displayed-cents');
        if (!displayedCentsAttr) return;

        var displayedCents = parseInt(displayedCentsAttr, 10);
        if (displayedCents !== group.totalCents) {
          console.warn('[ZUG][ASSERT] UI total mismatch', {
            uid: uid,
            displayed: displayedCents,
            computed: group.totalCents
          });
        }
      });
    },

    updateCartCountBadge: function(baseOnlyQty) {
      var updated = false;

      document.querySelectorAll('cart-count').forEach(function(badge) {
        var innerSpan = badge.querySelector('span[aria-hidden="true"]');
        var screenReaderSpan = badge.querySelector('.sr-only');
        if (innerSpan) {
          innerSpan.textContent = baseOnlyQty;
          updated = true;
        }
        if (screenReaderSpan) {
          screenReaderSpan.textContent = baseOnlyQty + (baseOnlyQty === 1 ? ' item' : ' items');
          updated = true;
        }

        if (baseOnlyQty === 0) {
          badge.classList.add('opacity-0');
        } else {
          badge.classList.remove('opacity-0');
        }
      });

      document.querySelectorAll('.count-bubble').forEach(function(bubble) {
        var text = bubble.textContent.trim();
        if (/^\d+$/.test(text) || bubble.querySelector('span[aria-hidden="true"]')) {
          var inner = bubble.querySelector('span[aria-hidden="true"]');
          if (inner) {
            inner.textContent = baseOnlyQty;
          } else if (/^\d+$/.test(text)) {
            bubble.textContent = baseOnlyQty;
          }
          updated = true;

          if (baseOnlyQty === 0) {
            bubble.classList.add('opacity-0');
          } else {
            bubble.classList.remove('opacity-0');
          }
        }
      });

      return updated;
    },

    sweepOrphans: function(cart, groups) {
      var self = this;
      var orphanUpdates = {};
      var orphanCount = 0;

      var baseUids = new Set();
      Object.keys(groups).forEach(function(uid) {
        if (groups[uid].hasBase) {
          baseUids.add(uid);
        }
      });

      cart.items.forEach(function(item) {
        var props = item.properties || {};
        if (props._pricing_engine === 'true' && props._line_type === 'adjustment' && props.calc_line_uid) {
          if (!baseUids.has(props.calc_line_uid)) {
            orphanUpdates[item.key] = 0;
            orphanCount++;
          }
        }
      });

      if (orphanCount > 0) {
        console.log('[ZUG][BUNDLE] orphan sweep removed N=' + orphanCount);
        self.invalidateCartCache();
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: orphanUpdates })
        }).then(function() {
          return true;
        });
      }

      return Promise.resolve(false);
    },

    handleCartMutation: function() {
      var self = this;

      return self.getCartFresh().then(function(cart) {
        var groupData = self.groupCart(cart);

        console.log('[ZUG][BUNDLE] mutation handled baseOnlyQty=' + groupData.baseOnlyQty + ' uidCount=' + groupData.uidCount);

        return self.sweepOrphans(cart, groupData.groups).then(function(hadOrphans) {
          if (hadOrphans) {
            return self.getCartFresh().then(function(freshCart) {
              var freshGroupData = self.groupCart(freshCart);
              self.applyUIUpdates(freshGroupData.groups, freshGroupData.baseOnlyQty);
              self.checkPriceInvariant(freshCart);
              self.triggerThemeRefresh();
              return freshCart;
            });
          } else {
            self.applyUIUpdates(groupData.groups, groupData.baseOnlyQty);
            self.checkPriceInvariant(cart);
            self.triggerThemeRefresh();
            return cart;
          }
        });
      }).catch(function(e) {
        console.error('[ZUG][BUNDLE] handleCartMutation error:', e);
      });
    },

    applyUIUpdates: function(groups, baseOnlyQty) {
      this.isApplyingUI = true;
      try {
        this.updateLineTotals(groups);
        this.updateCartCountBadge(baseOnlyQty);
        this.checkInvariant(groups);
      } finally {
        var self = this;
        setTimeout(function() { self.isApplyingUI = false; }, 0);
      }
    },

    refreshAndApply: function() {
      var self = this;
      if (self.isProcessing) return Promise.resolve();

      var now = Date.now();
      if ((now - self.lastRefreshTs) < self.refreshThrottleMs) {
        return Promise.resolve();
      }
      self.lastRefreshTs = now;

      return self.getCartOnceDebounced().then(function(cart) {
        var groupData = self.groupCart(cart);

        console.log('[ZUG][BUNDLE] refresh baseOnlyQty=' + groupData.baseOnlyQty + ' uidCount=' + groupData.uidCount);

        return self.sweepOrphans(cart, groupData.groups).then(function(hadOrphans) {
          if (hadOrphans) {
            return self.getCartFresh().then(function(freshCart) {
              var freshGroupData = self.groupCart(freshCart);
              self.applyUIUpdates(freshGroupData.groups, freshGroupData.baseOnlyQty);
              self.checkPriceInvariant(freshCart);
              return freshCart;
            });
          } else {
            self.applyUIUpdates(groupData.groups, groupData.baseOnlyQty);
            self.checkPriceInvariant(cart);
            return cart;
          }
        });
      }).catch(function(e) {
        console.error('[ZUG][BUNDLE] refreshAndApply error:', e);
      });
    },

    triggerThemeRefresh: function() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    },

    removeBundle: function(uid) {
      var self = this;
      if (self.isProcessing) return Promise.resolve();
      self.isProcessing = true;

      console.log('[ZUG][BUNDLE] remove bundle uid=' + uid.substring(0, 8) + '...');

      return self.getCartOnceDebounced().then(function(cart) {
        var keysToRemove = [];

        cart.items.forEach(function(item) {
          var props = item.properties || {};
          if (getLineUid(props) === uid) {
            keysToRemove.push(item.key);
          }
        });

        if (keysToRemove.length === 0) {
          self.isProcessing = false;
          return Promise.resolve();
        }

        var updates = {};
        keysToRemove.forEach(function(key) {
          updates[key] = 0;
        });

        console.log('[ZUG][BUNDLE] remove bundle keys=' + keysToRemove.length);

        self.invalidateCartCache();

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        }).then(function() {
          self.isProcessing = false;
          return self.handleCartMutation();
        });
      }).catch(function(e) {
        self.isProcessing = false;
        console.error('[ZUG][BUNDLE] remove error:', e);
      });
    },

    buildDtfQuantityMutation: function(cart, uid, newQty) {
      var quantity = Number(newQty);
      if (!Number.isInteger(quantity) || quantity < 1 || String(uid || '').indexOf('dtf-gang-') !== 0) {
        throw new Error('DTF Gang Sheet quantity mutation is invalid.');
      }

      var lines = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function(item) {
        return String((item.properties || {}).calc_line_uid || '') === String(uid);
      });
      var baseLines = lines.filter(function(item) {
        var props = item.properties || {};
        return props._line_type === 'base' &&
          props._product_family === 'dtf' &&
          props._dtf_exact_price_bundle === 'true' &&
          props._dtf_quantity_scalable === 'true';
      });

      if (baseLines.length !== 1 || lines.length < 2) {
        throw new Error('DTF Gang Sheet bundle is incomplete; quantity was not changed.');
      }

      var baseLine = baseLines[0];
      var unitTotalCents = Number((baseLine.properties || {})._dtf_unit_total_cents);
      if (!Number.isInteger(unitTotalCents) || unitTotalCents < 1) {
        throw new Error('DTF Gang Sheet unit total is invalid; quantity was not changed.');
      }

      var updates = {};
      var rollbackUpdates = {};
      var adjustmentUnits = {};
      lines.forEach(function(item) {
        var props = item.properties || {};
        rollbackUpdates[item.key] = Number(item.quantity);
        if (item.key === baseLine.key) {
          updates[item.key] = quantity;
          return;
        }
        if (props._line_type !== 'adjustment' || props._dtf_bundle_role !== 'adjustment') {
          throw new Error('DTF Gang Sheet bundle contains an unexpected line.');
        }
        var unit = String(props._dtf_adjustment_unit || '');
        var quantityPerSheet = Number(props._dtf_quantity_per_sheet);
        if ((unit !== 'dollar' && unit !== 'cent') || adjustmentUnits[unit] ||
            !Number.isInteger(quantityPerSheet) || quantityPerSheet < 1) {
          throw new Error('DTF Gang Sheet adjustment metadata is invalid.');
        }
        adjustmentUnits[unit] = true;
        updates[item.key] = quantityPerSheet * quantity;
      });

      return {
        uid: uid,
        quantity: quantity,
        oldQuantity: Number(baseLine.quantity),
        unitTotalCents: unitTotalCents,
        expectedTotalCents: unitTotalCents * quantity,
        updates: updates,
        rollbackUpdates: rollbackUpdates
      };
    },

    verifyDtfQuantityMutation: function(cart, mutation) {
      var lines = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function(item) {
        return String((item.properties || {}).calc_line_uid || '') === String(mutation.uid || '');
      });
      var baseLines = lines.filter(function(item) {
        return (item.properties || {})._line_type === 'base';
      });
      var lineQuantitiesMatch = lines.length === Object.keys(mutation.updates).length && lines.every(function(item) {
        return Number(item.quantity) === Number(mutation.updates[item.key]);
      });
      var totalCents = lines.reduce(function(sum, item) {
        return sum + Number(item.final_line_price);
      }, 0);

      return baseLines.length === 1 &&
        Number(baseLines[0].quantity) === Number(mutation.quantity) &&
        baseLines[0].requires_shipping === true &&
        lineQuantitiesMatch &&
        totalCents === Number(mutation.expectedTotalCents);
    },

    changeDtfQuantity: function(uid, newQty) {
      var self = this;
      var mutation = null;
      var rollbackComplete = false;

      return self.getCartFresh().then(function(cart) {
        mutation = self.buildDtfQuantityMutation(cart, uid, newQty);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: mutation.updates })
        });
      }).then(function(response) {
        if (!response.ok) throw new Error('DTF Gang Sheet quantity update failed: ' + response.status);
        return response.json();
      }).then(function(cart) {
        if (self.verifyDtfQuantityMutation(cart, mutation)) return cart;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: mutation.rollbackUpdates })
        }).then(function(response) {
          rollbackComplete = response.ok;
          throw new Error('DTF Gang Sheet quantity read-back failed; the original bundle was restored.');
        });
      }).catch(function(error) {
        if (!mutation || rollbackComplete) throw error;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: mutation.rollbackUpdates })
        }).then(function() {
          throw error;
        });
      }).then(function() {
        self.invalidateCartCache();
        return self.handleCartMutation();
      });
    },

    changeQuantity: function(key, uid, newQty) {
      var self = this;
      var lockKey = key || uid;

      if (self.mutationLocks[lockKey]) {
        return Promise.resolve();
      }

      self.mutationLocks[lockKey] = true;

      console.log('[ZUG][BUNDLE] mutation key=' + (key ? key.substring(0, 8) : 'N/A') + 
                  ' uid=' + (uid ? uid.substring(0, 8) : 'N/A') + 
                  ' newQty=' + newQty);

      if (newQty === 0) {
        delete self.mutationLocks[lockKey];
        return self.removeBundle(uid);
      }

      if (String(uid || '').indexOf('dtf-gang-') === 0) {
        return self.changeDtfQuantity(uid, newQty).then(function(result) {
          delete self.mutationLocks[lockKey];
          return result;
        }).catch(function(error) {
          delete self.mutationLocks[lockKey];
          self.invalidateCartCache();
          self.triggerThemeRefresh();
          console.error('[ZUG][BUNDLE] DTF Gang Sheet quantity error:', error);
        });
      }

      self.invalidateCartCache();

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: newQty })
      }).then(function() {
        delete self.mutationLocks[lockKey];
        return self.handleCartMutation();
      }).catch(function(e) {
        delete self.mutationLocks[lockKey];
        console.error('[ZUG][BUNDLE] changeQuantity error:', e);
      });
    },

    interceptRemoveClicks: function() {
      var self = this;

      document.addEventListener('click', function(e) {
        var removeLink = e.target.closest('a[href*="/cart/change"], a[href*="url_to_remove"], button[data-remove], [data-action="remove"], .link[aria-label*="Remove"]');
        if (!removeLink) return;

        var lineItem = removeLink.closest('[data-zug-uid]');
        if (!lineItem) {
          lineItem = removeLink.closest('tr[data-zug-uid]');
        }
        if (!lineItem) return;

        var uid = lineItem.dataset.zugUid;
        var engine = lineItem.dataset.zugEngine;
        if (!uid || engine !== 'true') return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        console.log('[ZUG][BUNDLE] Remove click intercepted uid=' + uid.substring(0, 8) + '...');
        self.removeBundle(uid);
      }, { capture: true });
    },

    interceptQuantityInputs: function() {
      var self = this;

      document.addEventListener('change', function(e) {
        var input = e.target;
        if (!input.matches('input[type="number"], input.quantity-input, [is="quantity-input"]')) return;

        var lineItem = input.closest('[data-zug-uid]');
        if (!lineItem) {
          lineItem = input.closest('tr[data-zug-uid]');
        }
        if (!lineItem) return;

        var uid = lineItem.dataset.zugUid;
        var engine = lineItem.dataset.zugEngine;
        var key = lineItem.dataset.zugKey || input.dataset.lineKey;
        if (!uid || engine !== 'true') return;

        var newQty = parseInt(input.value, 10);
        if (isNaN(newQty) || newQty < 0) return;

        e.preventDefault();
        e.stopPropagation();

        self.changeQuantity(key, uid, newQty);
      }, { capture: true });
    },

    interceptQuantityButtons: function() {
      var self = this;

      document.addEventListener('click', function(e) {
        var btn = e.target.closest('button[name="plus"], button[name="minus"], [data-action="increase"], [data-action="decrease"]');
        if (!btn) return;

        var lineItem = btn.closest('[data-zug-uid]');
        if (!lineItem) {
          lineItem = btn.closest('tr[data-zug-uid]');
        }
        if (!lineItem) return;

        var uid = lineItem.dataset.zugUid;
        var engine = lineItem.dataset.zugEngine;
        var key = lineItem.dataset.zugKey;
        if (!uid || engine !== 'true') return;

        var input = lineItem.querySelector('input[type="number"], input.quantity-input, [is="quantity-input"]');
        if (!input) return;

        var currentQty = parseInt(input.value, 10) || 0;
        var isPlus = btn.name === 'plus' || btn.dataset.action === 'increase';
        var isMinus = btn.name === 'minus' || btn.dataset.action === 'decrease';

        var newQty = currentQty;
        if (isPlus) newQty = currentQty + 1;
        if (isMinus) newQty = Math.max(0, currentQty - 1);

        if (newQty === currentQty) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        input.value = newQty;

        self.changeQuantity(key, uid, newQty);
      }, { capture: true });
    },

    debouncedRefresh: (function() {
      var timeout = null;
      return function(self) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(function() {
          self.refreshAndApply();
        }, 200);
      };
    })(),

    setupObservers: function() {
      var self = this;

      if (!self.observedEls) {
        self.observedEls = new WeakSet();
      }

      var targets = [
        'cart-drawer',
        '.cart-drawer',
        '#cart-drawer',
        '.cart-drawer__line-items',
        '.order-summary__body',
        '.cart',
        'cart-count',
        '.count-bubble'
      ];

      targets.forEach(function(selector) {
        var elements = document.querySelectorAll(selector);
        elements.forEach(function(target) {
          if (self.observedEls.has(target)) return;
          self.observedEls.add(target);

          var observer = new MutationObserver(function(mutations) {
            if (self.isApplyingUI) return;
            var hasRelevantChanges = mutations.some(function(m) {
              return m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0);
            });
            if (hasRelevantChanges && !self.isProcessing) {
              self.debouncedRefresh(self);
            }
          });
          observer.observe(target, { childList: true, subtree: true });
        });
      });
    },

    init: function() {
      if (this.initialized) return;
      this.initialized = true;

      var self = this;
      console.log('[ZUG][BUNDLE] init v6');

      if (window.location.search.indexOf('v2debug=1') !== -1) {
        if (window.__ZUG_PRICING_TABLE_VERSION) {
          console.log('[ZUG][BUNDLE] pricing_table_version:', window.__ZUG_PRICING_TABLE_VERSION);
        }
      }

      this.interceptRemoveClicks();
      this.interceptQuantityInputs();
      this.interceptQuantityButtons();
      this.setupObservers();

      var events = ['cart:refresh', 'cart:change', 'variant:add', 'shopify:section:load'];
      events.forEach(function(eventName) {
        document.addEventListener(eventName, function() {
          if (!self.isProcessing) {
            self.debouncedRefresh(self);
          }
        });
      });

      this.refreshAndApply();
    }
  };

  window.ZugCartBundle = ZugCartBundle;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      ZugCartBundle.init();
    });
  } else {
    ZugCartBundle.init();
  }
})();
